import { useState } from 'react';
import { KeyPair, mockKeyPairs } from '../../../mock/data';
import { Plus, Upload, Key, Trash2, ShieldCheck, Info, X, Grid, List } from 'lucide-react';

export default function KeyPairManagement() {
    const [keyPairs, setKeyPairs] = useState<KeyPair[]>(mockKeyPairs);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    const handleDelete = (id: string) => {
        if (window.confirm('确定要删除此密钥对吗？此操作不可恢复。')) {
            setKeyPairs(keyPairs.filter(key => key.id !== id));
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
            <div className="max-w-7xl mx-auto space-y-8">
                {/* 页面标题 */}
                <div className="relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl p-8">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-indigo-600/5"></div>
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform duration-200">
                                <Key className="w-7 h-7 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                                    密钥对管理
                                </h1>
                                <p className="text-gray-600 mt-1">管理用于SSH连接的密钥对，提高账户安全性</p>
                            </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                                className="flex items-center space-x-2 px-4 py-3 bg-white/70 backdrop-blur-sm hover:bg-white border border-white/50 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                            >
                                {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
                                <span>{viewMode === 'grid' ? '列表' : '网格'}</span>
                            </button>
                            
                            <button 
                                onClick={() => setShowCreateModal(true)}
                                className="group flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                            >
                                <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                                <span>创建密钥对</span>
                            </button>
                            <button className="group flex items-center space-x-2 px-6 py-3 text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-xl hover:bg-white hover:shadow-lg hover:border-gray-300 transition-all duration-300">
                                <Upload className="w-5 h-5 group-hover:-translate-y-1 transition-transform duration-300" />
                                <span>上传公钥</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 密钥对列表 */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl">
                    <div className="p-6 border-b border-gray-200/50">
                        <h2 className="text-xl font-bold text-gray-900 flex items-center">
                            <div className="w-2 h-6 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full mr-3"></div>
                            密钥对列表
                        </h2>
                    </div>

                    {keyPairs.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <Key className="w-10 h-10 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">暂无密钥对</h3>
                            <p className="text-gray-500 mb-6">创建或上传您的第一个密钥对</p>
                            <div className="flex items-center justify-center space-x-4">
                                <button
                                    onClick={() => setShowCreateModal(true)}
                                    className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                                >
                                    <Plus className="w-5 h-5" />
                                    <span>创建密钥对</span>
                                </button>
                                <button className="inline-flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors duration-200">
                                    <Upload className="w-5 h-5" />
                                    <span>上传公钥</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className={`${
                            viewMode === 'grid' 
                                ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 p-6'
                                : 'overflow-x-auto scrollbar-hide'
                        }`}>
                            {viewMode === 'list' ? (
                                // 列表视图 - 表格形式
                                <table className="w-full table-fixed min-w-[600px] lg:min-w-[1200px]">
                                    <thead className="bg-gray-50/50">
                                        <tr>
                                            <th className="w-[200px] lg:w-[280px] px-3 lg:px-6 py-4 text-left text-xs lg:text-sm font-semibold text-gray-900">名称</th>
                                            <th className="w-[280px] lg:w-[380px] px-3 lg:px-6 py-4 text-left text-xs lg:text-sm font-semibold text-gray-900">指纹</th>
                                            <th className="w-[140px] lg:w-[180px] px-3 lg:px-6 py-4 text-left text-xs lg:text-sm font-semibold text-gray-900">创建时间</th>
                                            <th className="w-[80px] lg:w-[120px] px-3 lg:px-6 py-4 text-center text-xs lg:text-sm font-semibold text-gray-900">状态</th>
                                            <th className="w-[100px] lg:w-[160px] px-3 lg:px-6 py-4 text-center text-xs lg:text-sm font-semibold text-gray-900">操作</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200/50">
                                        {keyPairs.map((key) => (
                                            <tr key={key.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                                            <ShieldCheck className="w-5 h-5 text-white" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-semibold text-gray-900 truncate">{key.name}</div>
                                                            <div className="text-sm text-gray-500">RSA 2048</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <code className="text-xs font-mono bg-gray-100 px-3 py-2 rounded block text-gray-800 leading-relaxed break-all">
                                                        {key.fingerprint}
                                                    </code>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-900 font-medium">
                                                        {new Date(key.createdAt).toLocaleDateString()}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {new Date(key.createdAt).toLocaleTimeString()}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                                                        key.status === 'active' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {key.status === 'active' ? '已应用' : '未使用'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center justify-center space-x-2">
                                                        <button className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 font-medium">
                                                            应用
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDelete(key.id)} 
                                                            className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 font-medium"
                                                        >
                                                            删除
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                // 网格视图 - 卡片形式
                                <>
                                    {keyPairs.map((key) => (
                                        <div key={key.id} className="relative group">
                                            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                                                {/* 装饰性背景 */}
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-50"></div>
                                                
                                                <div className="relative p-6">
                                                    {/* 头部信息 */}
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                                                                <ShieldCheck className="w-6 h-6 text-white" />
                                                            </div>
                                                            <div>
                                                                <h3 className="font-bold text-gray-900 text-lg">{key.name}</h3>
                                                                <p className="text-sm text-gray-500">RSA 2048</p>
                                                            </div>
                                                        </div>
                                                        
                                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                            key.status === 'active' 
                                                            ? 'bg-green-100 text-green-800' 
                                                            : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {key.status === 'active' ? '已应用' : '未使用'}
                                                        </span>
                                                    </div>

                                                    {/* 指纹 */}
                                                    <div className="mb-4">
                                                        <p className="text-sm font-medium text-gray-700 mb-2">指纹</p>
                                                        <code className="text-xs font-mono bg-gray-100 px-3 py-2 rounded-lg text-gray-800 break-all">
                                                            {key.fingerprint}
                                                        </code>
                                                    </div>

                                                    {/* 创建时间 */}
                                                    <div className="mb-6">
                                                        <p className="text-sm font-medium text-gray-700 mb-1">创建时间</p>
                                                        <p className="text-sm text-gray-600">
                                                            {new Date(key.createdAt).toLocaleString()}
                                                        </p>
                                                    </div>

                                                    {/* 操作按钮 */}
                                                    <div className="flex items-center space-x-3">
                                                        <button className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-sm font-medium">
                                                            应用密钥
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDelete(key.id)}
                                                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* 提示信息 */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-2xl border border-blue-200/50 shadow-lg p-6">
                    <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Info className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-blue-900 mb-2">密钥对使用说明</h3>
                            <p className="text-sm text-blue-800">
                                密钥对用于Linux节点的SSH认证，私钥由用户保管，平台不存储。应用密钥对将禁用密码登录以提高安全性。
                                请妥善保管您的私钥文件，丢失后无法恢复。
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 创建密钥对模态框 */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl p-8 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">创建密钥对</h2>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    密钥对名称
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    placeholder="输入密钥对名称"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    密钥类型
                                </label>
                                <select className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300">
                                    <option value="rsa">RSA 2048</option>
                                    <option value="rsa4096">RSA 4096</option>
                                    <option value="ed25519">Ed25519</option>
                                </select>
                            </div>

                            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-200">
                                <div className="flex items-center space-x-2">
                                    <Info className="w-5 h-5 text-yellow-600" />
                                    <span className="text-sm font-medium text-yellow-800">
                                        创建后请立即下载私钥文件，平台不会保存私钥。
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-4 mt-8">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                            >
                                取消
                            </button>
                            <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                                创建并下载
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
